import type { SearchState } from "@/components/v2/home/SmartSearch";
import { sanePrice, statusMeta, topYear } from "./format";
import { distanceToPlace, findPlaceByText, isNearPlace, nearKey, resolveNear, type NearPlace } from "./nearSearch";
import { bedsHint, type PropertyLite } from "./property";

export type SortKey = "newest" | "price-asc" | "price-desc" | "top";

const STAGE_RANK: Record<ReturnType<typeof statusMeta>["tone"], number> = {
  prelaunch: 0,
  upcoming: 1,
  selling: 2,
  completed: 3,
  soldout: 4,
};

export function parseSearchParams(sp: Record<string, string | string[] | undefined>): SearchState {
  const one = (k: string) => {
    const v = sp[k];
    return Array.isArray(v) ? v[0] ?? "" : v ?? "";
  };
  return {
    q: one("q"),
    budget: one("budget"),
    beds: one("beds"),
    districts: one("districts").split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
    top: one("top"),
    status: one("status"),
    tenure: one("tenure"),
    near: resolveNear(one("near")) ? one("near").trim().toLowerCase() : "",
  };
}

function inBudget(price: number | null, budget: string): boolean {
  if (!budget) return true;
  if (price == null) return false;
  switch (budget) {
    case "u15":
      return price < 1_500_000;
    case "15-2":
      return price >= 1_500_000 && price < 2_000_000;
    case "2-3":
      return price >= 2_000_000 && price < 3_000_000;
    case "3-4":
      return price >= 3_000_000 && price < 4_000_000;
    case "4-6":
      return price >= 4_000_000 && price < 6_000_000;
    case "o6":
      return price >= 6_000_000;
    default:
      return true;
  }
}

function tenureMatches(p: PropertyLite, tenure: string): boolean {
  if (!tenure) return true;
  if (tenure === "freehold") return !p.is_leasehold;
  if (tenure === "999") return !!p.is_leasehold && p.lease_year === 999;
  if (tenure === "99") return !!p.is_leasehold && p.lease_year !== 999;
  return true;
}

// Bedrooms is a SOFT filter: the list endpoint carries no bedroom mix, so
// we keep every project and float the ones whose description mentions the
// bedroom count. The project page shows the real per-bedroom availability.
function bedsScore(p: PropertyLite, beds: string): number {
  if (!beds) return 0;
  const n = Number(beds);
  const hint = p.beds_hint ?? bedsHint(`${p.description ?? ""} ${p.title ?? ""}`);
  return hint.includes(n) ? 1 : 0;
}

export function applySearch(all: PropertyLite[], s: SearchState, sort: SortKey = "newest"): PropertyLite[] {
  const near = s.near ? resolveNear(s.near) : null;
  const out = filterAndSort(all, s, sort, near);
  if (out.length > 0 || near || !s.q.trim()) return out;
  // Free-text fallback: "hougang mrt" or "rosyth school" with no project
  // match behaves like the near chip for that place.
  const place = findPlaceByText(s.q);
  if (!place) return out;
  return filterAndSort(all, { ...s, q: "", near: nearKey(place) }, sort, resolveNear(nearKey(place)));
}

function filterAndSort(all: PropertyLite[], s: SearchState, sort: SortKey, near: NearPlace | null): PropertyLite[] {
  const q = s.q.trim().toLowerCase();
  const out = all.filter((p) => {
    if (q) {
      const hay = `${p.name} ${p.title ?? ""} ${p.full_address ?? ""} ${p.district ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (near && !isNearPlace(p, near)) return false;
    if (!inBudget(sanePrice(p.min_flat_price), s.budget)) return false;
    if (s.districts.length && !s.districts.includes((p.district ?? "").toUpperCase())) return false;
    if (s.top && topYear(p.expected_top) !== s.top) return false;
    if (s.status && statusMeta(p.construction_status ?? "", p.isPreLaunch).tone !== s.status) return false;
    if (!tenureMatches(p, s.tenure)) return false;
    return true;
  });

  const price = (p: PropertyLite) => sanePrice(p.min_flat_price);
  const created = (p: PropertyLite) => (p.createdAt ? Date.parse(p.createdAt) : 0);
  // "Newest" groups by stage first (new launches on top, sold out at the
  // bottom), then newest record first inside each stage.
  const stage = (p: PropertyLite) => STAGE_RANK[statusMeta(p.construction_status ?? "", p.isPreLaunch).tone];
  // Nearest first replaces the default "newest" order when the place has
  // coordinates; an explicit sort choice still wins.
  const distance = (p: PropertyLite) => (near ? distanceToPlace(p, near) : null) ?? Infinity;
  out.sort((a, b) => {
    const bs = bedsScore(b, s.beds) - bedsScore(a, s.beds);
    // Newest keeps the stage order above the soft bedroom match, so a
    // sold-out project never floats to the top of the list.
    if (sort === "newest") {
      const st = stage(a) - stage(b);
      if (st !== 0) return st;
    }
    if (bs !== 0) return bs;
    switch (sort) {
      case "price-asc":
        return (price(a) ?? Infinity) - (price(b) ?? Infinity);
      case "price-desc":
        return (price(b) ?? -1) - (price(a) ?? -1);
      case "top":
        return (Number(topYear(a.expected_top)) || 9999) - (Number(topYear(b.expected_top)) || 9999);
      default: {
        if (near && near.lat != null) {
          const d = distance(a) - distance(b);
          if (d !== 0) return d;
        }
        return created(b) - created(a);
      }
    }
  });
  return out;
}
